import {ApiResponse} from '@legit.health/backend-client';
import {ONE_HOUR_IN_MS, THREE_MINUTES, generatePath} from '@legit.health/ui';
import {UserId} from '@legit.health/ui';
import {QueryClient} from '@tanstack/react-query';
import {PRACTICIONER} from '@/practitioner/consts/backend';
import {
  PRACTICIONER_DELETED_EVENT,
  PractitionerDeletedEvent
} from '@/practitioner/hooks/practitioner/api/practitionerDeletedEvent';
import {
  PractitionerPatchedEvent,
  PRACTICIONER_PATCHED_EVENT
} from '@/practitioner/hooks/practitioner/api/practitionerPatchedEvent';
import Practitioner from '@/practitioner/interfaces/Practitioner';
import {SubscribeToEventsOptions} from '@/shared/contexts/SubscribeToEventsContext';
import useApiQueries from '@/shared/hooks/useApiQueries';
import useApiQuery from '@/shared/hooks/useApiQuery';
import useSuspenseApiQueries from '@/shared/hooks/useSuspenseApiQueries';
import useSuspenseApiQuery from '@/shared/hooks/useSuspenseApiQuery';

export function getQueryKey(practitionerId: UserId | null) {
  return [PRACTICIONER, practitionerId];
}

const subscribeToEventsOptions: SubscribeToEventsOptions = {
  dataKey: 'useGetPractitioner',
  subscriptions: [
    [
      PRACTICIONER_PATCHED_EVENT,
      (queryClient: QueryClient, event: PractitionerPatchedEvent) =>
        queryClient.setQueryData<ApiResponse<Practitioner> | null>(
          getQueryKey(event.apiResponse.data.id),
          event.apiResponse
        )
    ],
    [
      PRACTICIONER_DELETED_EVENT,
      (queryClient: QueryClient, event: PractitionerDeletedEvent) =>
        queryClient.setQueryData<ApiResponse<Practitioner> | null>(
          getQueryKey(event.practitionerId),
          function (oldResponse) {
            if (!oldResponse) {
              return null;
            }
            return {
              ...oldResponse,
              data: {
                ...oldResponse.data,
                isDeleted: true
              }
            };
          }
        )
    ]
  ]
};

export function useGetPractitioner({
  practitionerId,
  isEnabled = true
}: {
  practitionerId: UserId | null;
  isEnabled?: boolean;
}) {
  const queryKey = getQueryKey(practitionerId);
  return useApiQuery<Practitioner>({
    queryOptions: {
      queryKey,
      staleTime: THREE_MINUTES,
      enabled: isEnabled && practitionerId !== null
    },
    requestArguments: {
      method: 'GET',
      path: practitionerId ? generatePath(PRACTICIONER, {practitionerId}) : ''
    },
    subscribeToEventsOptions
  });
}

export function useSuspenseGetPractitioner({practitionerId}: {practitionerId: UserId}) {
  const queryKey = getQueryKey(practitionerId);
  return useSuspenseApiQuery<Practitioner>({
    queryOptions: {
      queryKey,
      staleTime: THREE_MINUTES
    },
    requestArguments: {
      method: 'GET',
      path: practitionerId ? generatePath(PRACTICIONER, {practitionerId}) : ''
    },
    subscribeToEventsOptions
  });
}

export function useGetPractitionerRequests(
  practitionerIds: UserId[],
  {enabled = true}: {enabled?: boolean} = {
    enabled: true
  }
) {
  const queries = practitionerIds.map(
    (id) =>
      ({
        key: getQueryKey(id),
        requestArguments: {
          method: 'GET',
          path: id ? generatePath(PRACTICIONER, {practitionerId: id}) : ''
        }
      }) as const
  );
  return useApiQueries<Practitioner>({
    queries,
    queryOptions: {staleTime: ONE_HOUR_IN_MS, enabled}
  });
}

export function useSuspenseGetPractitionerRequests(practitionerIds: UserId[]) {
  const queries = practitionerIds.map(
    (id) =>
      ({
        key: getQueryKey(id),
        requestArguments: {
          method: 'GET',
          path: generatePath(PRACTICIONER, {practitionerId: id})
        }
      }) as const
  );
  return useSuspenseApiQueries<Practitioner>({
    queries,
    queryOptions: {staleTime: ONE_HOUR_IN_MS}
  });
}
